import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import { API_BASE_URL } from '@app/config/constants'
import {
  getStoredAccessToken,
  getStoredActiveOrganizationId,
} from '@features/auth/utils/sessionStorage'

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

api.interceptors.request.use(
  config => {
    const token = getStoredAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const explicitOrganizationHeader =
      config.headers?.['X-Organization-ID'] ?? config.headers?.['x-organization-id']
    const activeOrganizationId = getStoredActiveOrganizationId()
    if (activeOrganizationId && !explicitOrganizationHeader) {
      config.headers['X-Organization-ID'] = activeOrganizationId
    }

    // Remove Content-Type for FormData - let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    return config
  },
  error => Promise.reject(error)
)

// 401 handling is done in authService.setupInterceptors (with token refresh)

export const apiGet = <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
  api.get(url, config).then(res => res.data)

export const apiPost = <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
  api.post(url, data, config).then(res => res.data)

export const apiPut = <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
  api.put(url, data, config).then(res => res.data)

export const apiPatch = <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
  api.patch(url, data, config).then(res => res.data)

export const apiDelete = <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
  api.delete(url, config).then(res => res.data)

export default api
